import React, { FC } from 'react';

import { Center, Text } from '@rbilabs/universal-components';
import { TabBar as RNTabBar } from 'react-native-tab-view';

import { theme } from 'styles/configure-theme';

import { ColorOptions, GetTextColorParams, ITabBarProps, TabBarTextProps } from './types';

const getBackgroundColor = (color?: ColorOptions) => {
  return color === 'inverted' ? theme.token('background-active') : 'transparent';
};

const getIndicatorColor = (color?: ColorOptions) => {
  return color === 'inverted' ? Styles.color.white : theme.token('background-active');
};

const getTextColor = ({ color, isDisabled, isFocused }: GetTextColorParams) => {
  if (isDisabled) {
    return theme.token(color === 'inverted' ? 'text-disabled-reversed' : 'text-disabled');
  } else if (color === 'inverted') {
    return Styles.color.white;
  } else if (color === 'secondary') {
    return Styles.color.accent;
  } else if (isFocused) {
    return theme.token('text-active');
  }
  return theme.token('text-default');
};

const TabBarText = ({
  text,
  isFocused,
  color,
  isDisabled,
  variant = 'copyOne',
  ...rest
}: TabBarTextProps) => {
  return (
    <Text variant={variant} color={getTextColor({ color, isDisabled, isFocused })} {...rest}>
      {text}
    </Text>
  );
};

const TabBar: FC<React.PropsWithChildren<ITabBarProps>> = ({
  color,
  backgroundColor,
  enableFocusText,
  tabBarTextProps,
  ...props
}) => (
  <RNTabBar
    {...props}
    onTabPress={ev => {
      if (ev.route.disabled) {
        ev.preventDefault();
      }
    }}
    pressColor="transparent"
    style={{
      backgroundColor: backgroundColor || getBackgroundColor(color),
    }}
    contentContainerStyle={{
      borderBottomWidth: 1,
      borderBottomColor: theme.token('border-color-default'),
    }}
    indicatorStyle={{ backgroundColor: getIndicatorColor(color), height: 3 }}
    renderLabel={({ route, focused }) => {
      if (!route.title) {
        return null;
      }

      const props: Partial<TabBarTextProps> = {
        ...(enableFocusText && focused && { fontFamily: 'heading', isFocused: focused }),
        ...tabBarTextProps,
        isDisabled: !!route.disabled,
        color,
      };

      return (
        <Center>
          <TabBarText text={route.title} {...props} />
          {route.subTitle && <TabBarText text={route.subTitle} {...props} />}
        </Center>
      );
    }}
  />
);

export default TabBar;
